import React, { useEffect, useRef, useState, useCallback } from "react";

export const OverlayScrollbar = ({ containerRef }) => {
  const [visible, setVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const metricsRef = useRef({
    trackHeight: 0,
    thumbHeight: 0,
    maxScroll: 1,
    availableTrack: 0,
  });

  const updateScroll = useCallback(() => {
    const el = containerRef?.current;
    if (!el) {
      if (visible) setVisible(false);
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = el;
    const isOverflowing = clientHeight > 0 && scrollHeight > clientHeight;

    if (!isOverflowing) {
      if (visible) setVisible(false);
      return;
    }

    if (!visible) {
      setVisible(true);
    }

    const trackHeight = Math.max(1, clientHeight - 8);
    const thumbHeight = Math.min(trackHeight, Math.max(36, (clientHeight / scrollHeight) * trackHeight));
    const maxScroll = Math.max(1, scrollHeight - clientHeight);
    const availableTrack = Math.max(0, trackHeight - thumbHeight);
    const thumbTop = Math.max(0, Math.min(availableTrack, (scrollTop / maxScroll) * availableTrack));

    metricsRef.current = { trackHeight, thumbHeight, maxScroll, availableTrack };

    if (thumbRef.current) {
      thumbRef.current.style.height = `${thumbHeight}px`;
      thumbRef.current.style.top = `${thumbTop}px`;
    }
  }, [containerRef, visible]);

  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return;

    updateScroll();
    el.addEventListener("scroll", updateScroll, { passive: true });
    window.addEventListener("resize", updateScroll);

    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateScroll);
      observer.observe(el);
    }

    return () => {
      el.removeEventListener("scroll", updateScroll);
      window.removeEventListener("resize", updateScroll);
      observer?.disconnect();
    };
  }, [containerRef, updateScroll]);

  useEffect(() => {
    if (visible) {
      updateScroll();
    }
  }, [visible, updateScroll]);

  if (!visible) {
    return null;
  }

  const handleTrackClick = (e) => {
    if (e.target !== trackRef.current || !containerRef.current) return;
    const { trackHeight, maxScroll } = metricsRef.current;
    const rect = trackRef.current.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const ratio = Math.max(0, Math.min(1, clickY / trackHeight));

    const prevBehavior = containerRef.current.style.scrollBehavior;
    containerRef.current.style.scrollBehavior = "auto";
    containerRef.current.scrollTop = ratio * maxScroll;
    containerRef.current.style.scrollBehavior = prevBehavior || "";
    updateScroll();
  };

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef?.current) return;

    const container = containerRef.current;
    const prevBehavior = container.style.scrollBehavior;
    container.style.scrollBehavior = "auto";

    setIsDragging(true);

    const startY = e.clientY;
    const startScrollTop = container.scrollTop;
    const { availableTrack, maxScroll } = metricsRef.current;

    const handleMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      if (!containerRef.current) return;
      const deltaY = moveEvent.clientY - startY;
      const scrollDelta = availableTrack > 0 ? (deltaY / availableTrack) * maxScroll : 0;
      containerRef.current.scrollTop = Math.max(0, Math.min(maxScroll, startScrollTop + scrollDelta));
      updateScroll();
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (containerRef.current) {
        containerRef.current.style.scrollBehavior = prevBehavior || "";
      }
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <div
      ref={trackRef}
      data-testid="overlay-scrollbar"
      onClick={handleTrackClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: "absolute",
        top: "68px",
        right: "3px",
        bottom: "4px",
        width: "8px",
        zIndex: 30,
        backgroundColor: "transparent",
        pointerEvents: "auto",
        userSelect: isDragging ? "none" : "auto",
      }}
    >
      <div
        ref={thumbRef}
        data-testid="overlay-scrollbar-thumb"
        onMouseDown={handleThumbMouseDown}
        style={{
          position: "absolute",
          top: "0px",
          height: "36px",
          left: 0,
          right: 0,
          borderRadius: "999px",
          backgroundColor: isDragging || isHovered ? "rgba(148, 163, 184, 0.85)" : "rgba(148, 163, 184, 0.5)",
          border: "1px solid rgba(255, 255, 255, 0.28)",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.35)",
          cursor: "pointer",
          transition: isDragging ? "none" : "background-color 150ms ease",
          willChange: "top",
        }}
      />
    </div>
  );
};
